import { showDeleteConfirmation, showConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { pipeIf } from '@core/utils/rxjs/operators';
import { LibraryDocumentAPI } from '@business/api/libraryDocument';
import { LibraryDocumentParams, LibraryDocumentId, LibraryDocumentResponse } from '@business/entities/libraryDocument';

export class LibraryDocumentBloc extends BaseBloc {
  private readonly formRepo = new Repository<LibraryDocumentResponse>({
    getItemId: form => form.id,
  });

  readonly forms$ = this.formRepo.items$;
  readonly selectForm = this.formRepo.selectItem;

  onReset() {
    this.formRepo.reset();
  }

  fetchForms = (data?: LibraryDocumentParams) => {
    return LibraryDocumentAPI.fetchForms(data).pipe(this.formRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_form_error') }));
  };

  fetchForm = (id: LibraryDocumentId) => {
    return LibraryDocumentAPI.fetchForm(id).pipe(
      this.formRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_form_error') }),
    );
  };

  createForm = (data: Partial<LibraryDocumentResponse>) => {
    return LibraryDocumentAPI.createForm(data).pipe(
      this.formRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_form'),
      }),
    );
  };

  updateForm = (id: LibraryDocumentId, data: Partial<LibraryDocumentResponse>) => {
    return LibraryDocumentAPI.updateForm(id, data).pipe(
      this.formRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_form'),
      }),
    );
  };
  updateFormKey = (id: LibraryDocumentId, key: string, value: boolean | string | number) => {
    return LibraryDocumentAPI.updateFormKey(id, key, value).pipe(
      this.formRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_form'),
      }),
    );
  };

  deleteForm = (id: LibraryDocumentId) => {
    return showDeleteConfirmation(
      'Delete Library DOcument',
      'Are you sure you want to delete this? it will prohibit you from creating new documents with this format, but will NOT delete any existing documents in your library. You will need to remove those individually.',
    ).pipe(
      switchMap(() => LibraryDocumentAPI.deleteForm(id)),
      this.formRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_form'),
      }),
    );
  };

  duplicateForm = (id: LibraryDocumentId, form: Partial<LibraryDocumentResponse>) => {
    return LibraryDocumentAPI.duplicateForm(id, form).pipe(
      this.formRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('clone_form'),
      }),
    );
  };

  saveFormFields = (id: LibraryDocumentId, data: Pick<LibraryDocumentResponse, 'fields'>, message = true) => {
    return showConfirmation(
      'Update Document Template',
      'Are you sure you want to make these changes? This new document will only be applied to new documents that you will create. Any existing documents will NOT be updated with this new template.',
    ).pipe(
      switchMap(() => LibraryDocumentAPI.saveFormFields(id, data)),
      this.formRepo.ops.upsertOne(item => ({ item })),
      pipeIf(
        message &&
          handleMessage({
            type: requestMessage('save_form_fields'),
          }),
      ),
    );
  };
}
